import { useEffect, useMemo, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate, useSearchParams } from 'react-router';
import {
  CONTENT_BLOCK_MEDIA_TITLE_PREFIX,
  isContentBlockMediaTitle,
  stringifyContentBlocks,
} from '../lib/contentBlocks';
import { createRichContentEditor } from '../lib/richContentEditor';
import {
  createRoute,
  deleteRouteMedia,
  getApiErrorMessage,
  getRoute,
  importRouteFromJson,
  importRouteIntoExistingFromJson,
  listAllRouteMedia,
  listRouteCategories,
  listRouteDifficulties,
  listRouteTags,
  patchRoute,
  publishRoute,
  setRouteCover,
  type RouteStatus,
  uploadRouteMedia,
  type RouteStopType,
  type RouteWaypointItem,
} from '../lib/api';
import { CREATE_ROUTE_TEMPLATE } from '../templates/createRouteTemplate';

const RU_TO_LAT: Record<string, string> = {
  а: 'a',
  б: 'b',
  в: 'v',
  г: 'g',
  д: 'd',
  е: 'e',
  ё: 'e',
  ж: 'zh',
  з: 'z',
  и: 'i',
  й: 'y',
  к: 'k',
  л: 'l',
  м: 'm',
  н: 'n',
  о: 'o',
  п: 'p',
  р: 'r',
  с: 's',
  т: 't',
  у: 'u',
  ф: 'f',
  х: 'h',
  ц: 'ts',
  ч: 'ch',
  ш: 'sh',
  щ: 'sch',
  ъ: '',
  ы: 'y',
  ь: '',
  э: 'e',
  ю: 'yu',
  я: 'ya',
};

const slugify = (value: string): string =>
  Array.from(value.toLowerCase())
    .map((char) => RU_TO_LAT[char] ?? char)
    .join('')
    .trim()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-');

type PendingMedia = { file: File; url: string };
type DragMediaPayload = { kind: 'pending' | 'existing'; index: number };

type WaypointDraft = {
  id: number;
  sort_order: number;
  location_id?: number | null;
  stop_type: RouteStopType;
  latitude: string;
  longitude: string;
  title: string;
  location_name: string;
  note: string;
};

const STOP_TYPE_OPTIONS: Array<{ value: RouteStopType; label: string }> = [
  { value: 'start', label: 'Start' },
  { value: 'via', label: 'Via' },
  { value: 'finish', label: 'Finish' },
  { value: 'poi', label: 'POI' },
  { value: 'fuel', label: 'Fuel' },
  { value: 'food', label: 'Food' },
  { value: 'rest', label: 'Rest' },
];

const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

export default function CreateRoutePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const template = useMemo(() => CREATE_ROUTE_TEMPLATE, []);

  useEffect(() => {
    const root = containerRef.current;
    if (!root) return;

    const alert = root.querySelector<HTMLElement>('#create-route-alert');
    const alertTitle = root.querySelector<HTMLElement>('#create-route-alert-title');
    const alertText = root.querySelector<HTMLElement>('#create-route-alert-text');
    const form = root.querySelector<HTMLFormElement>('#create-route-form');
    const titleInput = root.querySelector<HTMLInputElement>('#route-title');
    const slugInput = root.querySelector<HTMLInputElement>('#route-slug');
    const locationNameInput = root.querySelector<HTMLInputElement>('#route-location-name');
    const descriptionInput = root.querySelector<HTMLTextAreaElement>('#route-description');
    const contentBlocksInput = root.querySelector<HTMLTextAreaElement>('#route-content-blocks');
    const contentBlocksEditorRoot = root.querySelector<HTMLElement>('#route-content-blocks-editor');
    const distanceInput = root.querySelector<HTMLInputElement>('#route-distance-km');
    const durationInput = root.querySelector<HTMLInputElement>('#route-duration-minutes');
    const gainInput = root.querySelector<HTMLInputElement>('#route-elevation-gain');
    const lossInput = root.querySelector<HTMLInputElement>('#route-elevation-loss');
    const difficultySelect = root.querySelector<HTMLSelectElement>('#route-difficulty');
    const categoriesSelect = root.querySelector<HTMLSelectElement>('#route-categories');
    const tagsSelect = root.querySelector<HTMLSelectElement>('#route-tags');
    const featuredInput = root.querySelector<HTMLInputElement>('#route-is-featured');
    const statusSelect = root.querySelector<HTMLSelectElement>('#route-status');
    const reviewCommentInput = root.querySelector<HTMLTextAreaElement>('#route-review-comment');
    const waypointsBody = root.querySelector<HTMLElement>('#route-waypoints-body');
    const addWaypointBtn = root.querySelector<HTMLButtonElement>('#route-waypoint-add-btn');
    const coverFile = root.querySelector<HTMLInputElement>('#route-cover-file');
    const coverName = root.querySelector<HTMLElement>('#route-cover-file-name');
    const coverPreview = root.querySelector<HTMLElement>('#route-cover-preview');
    const coverDropZone = root.querySelector<HTMLElement>('label[for="route-cover-file"]');
    const galleryFiles = root.querySelector<HTMLInputElement>('#route-gallery-files');
    const galleryPreview = root.querySelector<HTMLElement>('#route-gallery-preview');
    const cancelBtn = root.querySelector<HTMLButtonElement>('#route-cancel-btn');
    const importBtn = root.querySelector<HTMLButtonElement>('#route-import-json-btn');
    const saveDraftBtn = root.querySelector<HTMLButtonElement>('#route-save-draft-btn');
    const publishBtn = root.querySelector<HTMLButtonElement>('#route-publish-btn');

    if (
      !alert ||
      !alertTitle ||
      !alertText ||
      !form ||
      !titleInput ||
      !slugInput ||
      !locationNameInput ||
      !descriptionInput ||
      !contentBlocksInput ||
      !contentBlocksEditorRoot ||
      !distanceInput ||
      !durationInput ||
      !gainInput ||
      !lossInput ||
      !difficultySelect ||
      !categoriesSelect ||
      !tagsSelect ||
      !featuredInput ||
      !statusSelect ||
      !reviewCommentInput ||
      !waypointsBody ||
      !addWaypointBtn ||
      !coverFile ||
      !coverName ||
      !coverPreview ||
      !coverDropZone ||
      !galleryFiles ||
      !galleryPreview ||
      !cancelBtn ||
      !importBtn ||
      !saveDraftBtn ||
      !publishBtn
    ) {
      return;
    }

    const editIdRaw = searchParams.get('id');
    const editId = editIdRaw ? Number(editIdRaw) : NaN;
    const isEdit = Number.isFinite(editId) && editId > 0;

    let disposed = false;
    let slugTouched = false;
    let currentRouteId: number | null = isEdit ? editId : null;
    let selectedCover: File | null = null;
    let selectedCoverMediaId: number | null = null;
    let selectedCoverRemoteUrl: string | null = null;
    let currentCoverObjectUrl: string | null = null;
    let waypointCounter = 1;
    let waypoints: WaypointDraft[] = [];
    let selectedGallery: PendingMedia[] = [];
    let existingGallery: Array<{ id: number; url: string; title: string }> = [];

    const contentBlocksEditor = createRichContentEditor({
      root: contentBlocksEditorRoot,
      onChange: (blocks) => {
        contentBlocksInput.value = stringifyContentBlocks(blocks);
      },
      getTagOptions: () => [],
      uploadImage: async (file) => {
        if (!currentRouteId) {
          throw new Error('Сначала сохраните маршрут как черновик, затем загружайте изображения в блоки.');
        }
        const media = await uploadRouteMedia(currentRouteId, { file, title: `${CONTENT_BLOCK_MEDIA_TITLE_PREFIX}${file.name}` });
        return media.file_large_url || media.file_url;
      },
    });

    const imagePreviewModal = document.createElement('div');
    imagePreviewModal.className =
      'fixed inset-0 z-[1400] hidden items-center justify-center bg-slate-950/80 p-4 cursor-zoom-out';
    imagePreviewModal.innerHTML = `
      <button data-preview-close="true" class="absolute right-4 top-4 size-9 rounded-full bg-white/90 text-slate-800 hover:bg-white" type="button">
        <span class="material-symbols-outlined text-base">close</span>
      </button>
      <img data-preview-image="true" class="max-h-[88vh] max-w-[94vw] rounded-xl object-contain shadow-2xl" alt="Image preview" />
    `;
    document.body.append(imagePreviewModal);
    const previewImage = imagePreviewModal.querySelector<HTMLImageElement>('[data-preview-image="true"]');

    const setButtonsLoading = (loading: boolean) => {
      saveDraftBtn.disabled = loading;
      publishBtn.disabled = loading;
      importBtn.disabled = loading;
      saveDraftBtn.classList.toggle('opacity-60', loading);
      publishBtn.classList.toggle('opacity-60', loading);
      importBtn.classList.toggle('opacity-60', loading);
    };

    const setImportLoading = (loading: boolean) => {
      importBtn.disabled = loading;
      saveDraftBtn.disabled = loading;
      publishBtn.disabled = loading;
      importBtn.innerHTML = loading
        ? '<span class="material-symbols-outlined text-sm animate-spin">progress_activity</span> Importing...'
        : '<span class="material-symbols-outlined text-sm">upload_file</span> Import JSON';
    };

    const openJsonImportDialog = (): Promise<string | null> =>
      new Promise((resolve) => {
        const overlay = document.createElement('div');
        overlay.className = 'fixed inset-0 z-[1500] flex items-center justify-center bg-slate-950/70 p-4';
        overlay.innerHTML = `
          <div class="w-full max-w-3xl rounded-xl border border-slate-200 bg-white p-5 shadow-2xl">
            <div class="mb-3 flex items-center justify-between">
              <h3 class="text-base font-bold text-slate-900">Import Route JSON</h3>
              <button data-import-close="true" class="size-8 rounded-full border border-slate-300 text-slate-500 hover:bg-slate-100" type="button">
                <span class="material-symbols-outlined text-sm">close</span>
              </button>
            </div>
            <p class="mb-3 text-xs text-slate-500">Paste full JSON payload for route import.</p>
            <textarea data-import-json="true" class="h-72 w-full rounded-lg border border-slate-300 bg-slate-50 p-3 font-mono text-xs focus:border-primary focus:ring-primary" placeholder='{"title":"...","waypoints":[...]}'></textarea>
            <div class="mt-4 flex justify-end gap-2">
              <button data-import-cancel="true" class="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-100" type="button">Cancel</button>
              <button data-import-confirm="true" class="rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-blue-600" type="button">Import</button>
            </div>
          </div>
        `;
        document.body.append(overlay);
        document.body.classList.add('overflow-hidden');

        const textarea = overlay.querySelector<HTMLTextAreaElement>('textarea[data-import-json="true"]');
        const close = () => {
          overlay.remove();
          document.body.classList.remove('overflow-hidden');
        };

        const onClick = (event: Event) => {
          const target = event.target as HTMLElement;
          if (target === overlay || target.closest('[data-import-close="true"]') || target.closest('[data-import-cancel="true"]')) {
            close();
            resolve(null);
            return;
          }
          if (target.closest('[data-import-confirm="true"]')) {
            const value = textarea?.value ?? '';
            close();
            resolve(value);
          }
        };

        const onKeydown = (event: KeyboardEvent) => {
          if (event.key !== 'Escape') return;
          document.removeEventListener('keydown', onKeydown);
          close();
          resolve(null);
        };

        overlay.addEventListener('click', onClick, { once: false });
        document.addEventListener('keydown', onKeydown, { once: true });
        window.setTimeout(() => textarea?.focus(), 0);
      });

    const showAlert = (kind: 'error' | 'success', title: string, text: string) => {
      alert.classList.remove('hidden', 'bg-rose-50', 'border-rose-200', 'bg-emerald-50', 'border-emerald-200');
      if (kind === 'success') {
        alert.classList.add('bg-emerald-50', 'border-emerald-200');
        alertTitle.className = 'text-sm font-semibold text-emerald-800';
        alertText.className = 'text-sm text-emerald-700 opacity-80';
      } else {
        alert.classList.add('bg-rose-50', 'border-rose-200');
        alertTitle.className = 'text-sm font-semibold text-rose-800';
        alertText.className = 'text-sm text-rose-700 opacity-80';
      }
      alertTitle.textContent = title;
      alertText.textContent = text;
    };

    const renderWaypoints = () => {
      if (!waypoints.length) {
        waypointsBody.innerHTML = '<tr><td colspan="8" class="px-2 py-4 text-sm text-slate-500">No waypoints. Add at least two points.</td></tr>';
        return;
      }
      waypointsBody.innerHTML = waypoints
        .sort((a, b) => a.sort_order - b.sort_order)
        .map(
          (waypoint) => `
          <tr data-waypoint-id="${waypoint.id}" class="border-b border-slate-100">
            <td class="px-2 py-2 text-sm text-slate-500">${waypoint.sort_order}</td>
            <td class="px-2 py-2">
              <select data-waypoint-field="stop_type" class="w-full rounded border border-slate-200 bg-slate-50 text-sm px-2 py-1">
                ${STOP_TYPE_OPTIONS.map((option) => `<option value="${option.value}" ${option.value === waypoint.stop_type ? 'selected' : ''}>${option.label}</option>`).join('')}
              </select>
            </td>
            <td class="px-2 py-2"><input data-waypoint-field="latitude" class="w-full rounded border border-slate-200 bg-slate-50 text-sm px-2 py-1" value="${waypoint.latitude}" placeholder="53.900000"/></td>
            <td class="px-2 py-2"><input data-waypoint-field="longitude" class="w-full rounded border border-slate-200 bg-slate-50 text-sm px-2 py-1" value="${waypoint.longitude}" placeholder="27.560000"/></td>
            <td class="px-2 py-2"><input data-waypoint-field="title" class="w-full rounded border border-slate-200 bg-slate-50 text-sm px-2 py-1" value="${waypoint.title}" placeholder="Start"/></td>
            <td class="px-2 py-2"><input data-waypoint-field="location_name" class="w-full rounded border border-slate-200 bg-slate-50 text-sm px-2 py-1" value="${waypoint.location_name}" placeholder="Minsk"/></td>
            <td class="px-2 py-2"><input data-waypoint-field="note" class="w-full rounded border border-slate-200 bg-slate-50 text-sm px-2 py-1" value="${waypoint.note}" placeholder="Optional note"/></td>
            <td class="px-2 py-2 text-right">
              <button type="button" data-waypoint-action="remove" class="text-rose-600 hover:underline text-sm">Remove</button>
            </td>
          </tr>
        `
        )
        .join('');
    };

    const addWaypoint = () => {
      const defaultType: RouteStopType =
        waypoints.length === 0 ? 'start' : waypoints.length === 1 ? 'finish' : 'via';
      waypoints.push({
        id: waypointCounter++,
        sort_order: waypoints.length + 1,
        location_id: null,
        stop_type: defaultType,
        latitude: '',
        longitude: '',
        title: '',
        location_name: '',
        note: '',
      });
      if (waypoints.length === 2 && waypoints[0].stop_type !== 'start') {
        waypoints[0].stop_type = 'start';
      }
      if (waypoints.length >= 2) {
        waypoints[waypoints.length - 1].stop_type = 'finish';
      }
      renderWaypoints();
    };

    const onWaypointsInput = (event: Event) => {
      const target = event.target as HTMLElement;
      const row = target.closest<HTMLElement>('tr[data-waypoint-id]');
      if (!row) return;
      const id = Number(row.dataset.waypointId);
      const waypoint = waypoints.find((item) => item.id === id);
      if (!waypoint) return;
      if (target instanceof HTMLInputElement) {
        const field = target.dataset.waypointField as keyof WaypointDraft;
        if (!field) return;
        waypoint[field] = target.value as never;
      } else if (target instanceof HTMLSelectElement) {
        const field = target.dataset.waypointField as keyof WaypointDraft;
        if (!field) return;
        waypoint[field] = target.value as never;
      }
    };

    const onWaypointsClick = (event: Event) => {
      const target = event.target as HTMLElement;
      const button = target.closest<HTMLButtonElement>('button[data-waypoint-action="remove"]');
      if (!button) return;
      const row = target.closest<HTMLElement>('tr[data-waypoint-id]');
      if (!row) return;
      const id = Number(row.dataset.waypointId);
      waypoints = waypoints.filter((item) => item.id !== id);
      waypoints = waypoints
        .sort((a, b) => a.sort_order - b.sort_order)
        .map((item, index) => ({
          ...item,
          sort_order: index + 1,
          stop_type:
            index === 0 ? 'start' : index === waypoints.length - 2 ? 'finish' : item.stop_type === 'finish' ? 'via' : item.stop_type,
        }));
      if (waypoints.length >= 1) waypoints[0].stop_type = 'start';
      if (waypoints.length >= 2) waypoints[waypoints.length - 1].stop_type = 'finish';
      renderWaypoints();
    };

    const renderGallery = () => {
      const existingHtml = existingGallery
        .map(
          (item, index) => `
            <div class="relative aspect-square rounded-lg bg-slate-50 border border-slate-200 overflow-hidden cursor-pointer" draggable="true" data-drag-media="existing" data-drag-index="${index}" data-preview-url="${item.url}" data-preview-alt="${escapeHtml(item.title || `media-${item.id}`)}">
              <img class="w-full h-full object-cover pointer-events-none" src="${item.url}" alt="${escapeHtml(item.title || `media-${item.id}`)}"/>
              <button type="button" data-route-media-remove="${item.id}" class="absolute top-1 right-1 size-6 inline-flex items-center justify-center rounded-full bg-white/95 border border-slate-200 text-rose-600 hover:bg-rose-50" title="Remove media">
                <span class="material-symbols-outlined text-sm">close</span>
              </button>
            </div>
          `
        )
        .join('');
      const pendingHtml = selectedGallery
        .map(
          (item, index) => `
            <div class="relative aspect-square rounded-lg bg-slate-50 border border-slate-200 overflow-hidden cursor-pointer" draggable="true" data-drag-media="pending" data-drag-index="${index}" data-preview-url="${item.url}" data-preview-alt="${escapeHtml(item.file.name)}">
              <img class="w-full h-full object-cover pointer-events-none" src="${item.url}" alt="${escapeHtml(item.file.name)}"/>
              <button type="button" data-route-pending-remove="${index}" class="absolute top-1 right-1 size-6 inline-flex items-center justify-center rounded-full bg-white/95 border border-slate-200 text-rose-600 hover:bg-rose-50" title="Remove file">
                <span class="material-symbols-outlined text-sm">close</span>
              </button>
            </div>
          `
        )
        .join('');

      galleryPreview.innerHTML =
        existingHtml + pendingHtml || '<div class="col-span-4 text-xs text-slate-500">No gallery images selected.</div>';
    };

    const renderCoverPreview = (coverUrl?: string | null) => {
      if (currentCoverObjectUrl) {
        URL.revokeObjectURL(currentCoverObjectUrl);
        currentCoverObjectUrl = null;
      }
      const placeholderSpans = Array.from(coverDropZone.querySelectorAll('span'));
      const resetDropZone = () => {
        coverDropZone.style.backgroundImage = '';
        coverDropZone.classList.remove('bg-cover', 'bg-center', 'border-solid');
        coverDropZone.classList.add('bg-slate-100', 'border-dashed', 'text-slate-400');
        coverDropZone.removeAttribute('data-preview-url');
        coverDropZone.removeAttribute('data-preview-alt');
        placeholderSpans.forEach((span) => span.classList.remove('hidden'));
      };
      const applyDropZoneImage = (url: string, alt: string) => {
        const escapedUrl = url.replace(/"/g, '\\"');
        coverDropZone.style.backgroundImage = `url("${escapedUrl}")`;
        coverDropZone.classList.add('bg-cover', 'bg-center', 'border-solid');
        coverDropZone.classList.remove('bg-slate-100', 'border-dashed', 'text-slate-400');
        coverDropZone.setAttribute('data-preview-url', url);
        coverDropZone.setAttribute('data-preview-alt', alt);
        placeholderSpans.forEach((span) => span.classList.add('hidden'));
      };

      if (selectedCover) {
        currentCoverObjectUrl = URL.createObjectURL(selectedCover);
        coverName.textContent = selectedCover.name;
        applyDropZoneImage(currentCoverObjectUrl, 'Route cover preview');
        coverPreview.innerHTML = '';
        return;
      }

      if (selectedCoverRemoteUrl) {
        coverName.textContent = 'Selected from gallery';
        applyDropZoneImage(selectedCoverRemoteUrl, 'Route cover from gallery');
        coverPreview.innerHTML = '';
        return;
      }

      if (coverUrl) {
        coverName.textContent = 'Current cover image';
        applyDropZoneImage(coverUrl, 'Route cover');
        coverPreview.innerHTML = '';
        return;
      }

      resetDropZone();
      coverName.textContent = '';
      coverPreview.innerHTML = '';
    };

    const openImagePreview = (url: string, alt?: string) => {
      if (!previewImage) return;
      const value = String(url || '').trim();
      if (!value) return;
      previewImage.src = value;
      previewImage.alt = alt || 'Image preview';
      imagePreviewModal.classList.remove('hidden');
      imagePreviewModal.classList.add('flex');
      document.body.classList.add('overflow-hidden');
    };

    const closeImagePreview = () => {
      if (!previewImage) return;
      imagePreviewModal.classList.add('hidden');
      imagePreviewModal.classList.remove('flex');
      previewImage.src = '';
      document.body.classList.remove('overflow-hidden');
    };

    const coverDropActive = (active: boolean) => {
      coverDropZone.classList.toggle('ring-2', active);
      coverDropZone.classList.toggle('ring-primary', active);
      coverDropZone.classList.toggle('ring-offset-2', active);
      coverDropZone.classList.toggle('border-primary', active);
      coverDropZone.classList.toggle('bg-primary/5', active);
    };

    const onCoverChange = () => {
      selectedCover = coverFile.files?.[0] ?? null;
      selectedCoverMediaId = null;
      selectedCoverRemoteUrl = null;
      coverFile.value = '';
      renderCoverPreview();
    };

    const onGalleryChange = () => {
      const files = Array.from(galleryFiles.files || []);
      files.forEach((file) => selectedGallery.push({ file, url: URL.createObjectURL(file) }));
      galleryFiles.value = '';
      renderGallery();
    };

    const onGalleryClick = (event: Event) => {
      const target = event.target as HTMLElement;
      const existingButton = target.closest<HTMLButtonElement>('button[data-route-media-remove]');
      if (existingButton && currentRouteId) {
        const mediaId = Number(existingButton.dataset.routeMediaRemove);
        if (!Number.isFinite(mediaId) || mediaId <= 0) return;
        void (async () => {
          try {
            await deleteRouteMedia(currentRouteId, mediaId);
            existingGallery = existingGallery.filter((item) => item.id !== mediaId);
            if (selectedCoverMediaId === mediaId) {
              selectedCoverMediaId = null;
              selectedCoverRemoteUrl = null;
              renderCoverPreview();
            }
            renderGallery();
            showAlert('success', 'Media removed', 'Route gallery item was removed.');
          } catch (error) {
            showAlert('error', 'Failed to remove media', getApiErrorMessage(error, 'Cannot remove route media.'));
          }
        })();
        return;
      }

      const pendingButton = target.closest<HTMLButtonElement>('button[data-route-pending-remove]');
      if (pendingButton) {
        const index = Number(pendingButton.dataset.routePendingRemove);
        if (!Number.isFinite(index) || index < 0 || index >= selectedGallery.length) return;
        const [removed] = selectedGallery.splice(index, 1);
        if (removed) URL.revokeObjectURL(removed.url);
        renderGallery();
        return;
      }

      const previewTarget = target.closest<HTMLElement>('[data-preview-url]');
      if (previewTarget?.dataset.previewUrl) {
        openImagePreview(previewTarget.dataset.previewUrl, previewTarget.dataset.previewAlt);
      }
    };

    const onGalleryDragStart = (event: DragEvent) => {
      const target = event.target as HTMLElement;
      const tile = target.closest<HTMLElement>('[data-drag-media][data-drag-index]');
      if (!tile || !event.dataTransfer) return;
      const kind = tile.dataset.dragMedia;
      const index = Number(tile.dataset.dragIndex ?? '');
      if (!kind || Number.isNaN(index)) return;
      const payload: DragMediaPayload = { kind: kind === 'existing' ? 'existing' : 'pending', index };
      event.dataTransfer.setData('application/json', JSON.stringify(payload));
      event.dataTransfer.effectAllowed = 'move';
    };

    const onCoverDragOver = (event: DragEvent) => {
      event.preventDefault();
      if (event.dataTransfer) event.dataTransfer.dropEffect = 'move';
      coverDropActive(true);
    };

    const onCoverDragLeave = () => {
      coverDropActive(false);
    };

    const onCoverDrop = async (event: DragEvent) => {
      event.preventDefault();
      coverDropActive(false);
      const raw = event.dataTransfer?.getData('application/json');
      if (!raw) return;
      let payload: DragMediaPayload | null = null;
      try {
        payload = JSON.parse(raw) as DragMediaPayload;
      } catch {
        payload = null;
      }
      if (!payload) return;

      if (payload.kind === 'pending') {
        const media = selectedGallery[payload.index];
        if (!media) return;
        selectedCover = media.file;
        selectedCoverMediaId = null;
        selectedCoverRemoteUrl = null;
        selectedGallery = selectedGallery.filter((_, idx) => idx !== payload.index);
        renderCoverPreview();
        renderGallery();
        showAlert('success', 'Cover selected', 'Image moved from gallery to cover.');
        return;
      }

      const media = existingGallery[payload.index];
      if (!media) return;
      selectedCover = null;
      selectedCoverMediaId = media.id;
      selectedCoverRemoteUrl = media.url;
      renderCoverPreview();
      showAlert('success', 'Cover selected', 'Existing gallery image selected as cover.');
    };

    const onCoverDropEvent = (event: Event) => {
      void onCoverDrop(event as DragEvent);
    };

    const onPreviewModalClick = (event: Event) => {
      const target = event.target as HTMLElement;
      if (target === imagePreviewModal || target.closest('[data-preview-close="true"]')) {
        closeImagePreview();
      }
    };

    const onPreviewKeydown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeImagePreview();
      }
    };

    const loadDictionaries = async () => {
      try {
        const [categories, tags, difficulties] = await Promise.all([
          listRouteCategories(),
          listRouteTags(),
          listRouteDifficulties(),
        ]);
        if (disposed) return;
        difficultySelect.innerHTML = '<option value="">Select difficulty</option>';
        difficulties.forEach((item) => {
          const option = document.createElement('option');
          option.value = String(item.id);
          option.textContent = item.name;
          difficultySelect.append(option);
        });
        categoriesSelect.innerHTML = categories.map((item) => `<option value="${item.id}">${item.name}</option>`).join('');
        tagsSelect.innerHTML = tags.map((item) => `<option value="${item.id}">${item.name}</option>`).join('');
      } catch {
        // no-op
      }
    };

    const collectWaypoints = (): RouteWaypointItem[] => {
      if (waypoints.length < 2) {
        throw new Error('Route must contain at least 2 waypoints.');
      }

      const normalized = waypoints
        .sort((a, b) => a.sort_order - b.sort_order)
        .map((item, index) => {
          const lat = Number(item.latitude.replace(',', '.'));
          const lon = Number(item.longitude.replace(',', '.'));
          if (!Number.isFinite(lat) || lat < -90 || lat > 90) {
            throw new Error(`Waypoint #${index + 1}: invalid latitude.`);
          }
          if (!Number.isFinite(lon) || lon < -180 || lon > 180) {
            throw new Error(`Waypoint #${index + 1}: invalid longitude.`);
          }
          const waypoint: RouteWaypointItem = {
            sort_order: index + 1,
            stop_type: item.stop_type,
            latitude: lat.toFixed(6),
            longitude: lon.toFixed(6),
            title: item.title.trim(),
            location_name: item.location_name.trim(),
            note: item.note.trim(),
          };
          if (item.location_id && Number.isFinite(item.location_id) && item.location_id > 0) {
            waypoint.location = item.location_id;
          }
          return waypoint;
        });
      normalized[0].stop_type = 'start';
      normalized[normalized.length - 1].stop_type = 'finish';
      return normalized;
    };

    const buildPayload = () => {
      const parsedBlocks = contentBlocksEditor.getBlocks();
      const selectedCategoryIds = Array.from(categoriesSelect.selectedOptions).map((option) => Number(option.value));
      const selectedTagIds = Array.from(tagsSelect.selectedOptions).map((option) => Number(option.value));

      return {
        title: titleInput.value.trim(),
        slug: slugInput.value.trim() || undefined,
        location_name: locationNameInput.value.trim(),
        description: descriptionInput.value.trim(),
        content_format: parsedBlocks?.length ? ('blocks_v1' as const) : undefined,
        content_blocks: parsedBlocks || undefined,
        distance_km: distanceInput.value.trim() || '0',
        duration_minutes: Number(durationInput.value || 0),
        elevation_gain_m: Number(gainInput.value || 0),
        elevation_loss_m: Number(lossInput.value || 0),
        difficulty: difficultySelect.value ? Number(difficultySelect.value) : undefined,
        category_ids: selectedCategoryIds,
        tag_ids: selectedTagIds,
        is_featured: featuredInput.checked,
        status: statusSelect.value as RouteStatus,
        review_comment: reviewCommentInput.value.trim(),
        waypoints: collectWaypoints(),
      };
    };

    const uploadDeferredMedia = async (routeId: number) => {
      if (selectedCover) {
        await setRouteCover(routeId, { coverImage: selectedCover });
        selectedCover = null;
        selectedCoverMediaId = null;
      } else if (selectedCoverMediaId) {
        await setRouteCover(routeId, { mediaId: selectedCoverMediaId });
        selectedCoverMediaId = null;
      }
      if (selectedGallery.length) {
        for (let index = 0; index < selectedGallery.length; index += 1) {
          await uploadRouteMedia(routeId, {
            file: selectedGallery[index].file,
            title: selectedGallery[index].file.name,
            sort_order: index,
          });
        }
      }
    };

    const submit = async (mode: 'draft' | 'publish') => {
      setButtonsLoading(true);
      try {
        const payload = buildPayload();
        if (!payload.title) {
          throw new Error('Title is required.');
        }

        const entity = isEdit && currentRouteId ? await patchRoute(currentRouteId, payload) : await createRoute(payload);
        currentRouteId = entity.id;
        await uploadDeferredMedia(entity.id);
        const shouldPublish = mode === 'publish' && entity.status !== 'published';
        if (shouldPublish) {
          await publishRoute(entity.id);
        }

        showAlert(
          'success',
          mode === 'publish' ? 'Route published' : 'Route saved',
          mode === 'publish'
            ? shouldPublish
              ? 'Route was successfully published.'
              : 'Route is already published. Changes were saved.'
            : 'Draft was saved successfully.'
        );
        if (mode === 'publish') {
          window.setTimeout(() => navigate('/routes'), 250);
          return;
        }
        if (!isEdit) {
          navigate(`/routes/create?id=${entity.id}`, { replace: true });
        } else {
          const media = await listAllRouteMedia(entity.id);
          if (!disposed) {
            existingGallery = media.results
              .filter((item) => !isContentBlockMediaTitle(item.title))
              .map((item) => ({ id: item.id, url: item.file_large_url || item.file_url, title: item.title || '' }));
            selectedGallery.forEach((item) => URL.revokeObjectURL(item.url));
            selectedGallery = [];
            renderGallery();
          }
        }
      } catch (error) {
        showAlert('error', 'Failed to save route', getApiErrorMessage(error, error instanceof Error ? error.message : 'Check form fields.'));
      } finally {
        setButtonsLoading(false);
      }
    };

    const onImportJson = async () => {
      const raw = await openJsonImportDialog();
      if (raw === null) return;
      if (!raw.trim()) {
        showAlert('error', 'Import failed', 'JSON payload is empty.');
        return;
      }

      let parsed: Record<string, unknown>;
      try {
        parsed = JSON.parse(raw) as Record<string, unknown>;
      } catch {
        showAlert('error', 'Import failed', 'Invalid JSON format.');
        return;
      }

      setImportLoading(true);
      try {
        if (currentRouteId) {
          const updated = await importRouteIntoExistingFromJson(currentRouteId, parsed);
          showAlert('success', 'Route updated', `Route updated from JSON: #${updated.id}.`);
          await fillFromExisting(updated.id);
        } else {
          const created = await importRouteFromJson(parsed);
          showAlert('success', 'Route imported', `Route imported: #${created.id}. Opening editor...`);
          window.setTimeout(() => navigate(`/routes/create?id=${created.id}`), 250);
        }
      } catch (error) {
        showAlert('error', 'Import failed', getApiErrorMessage(error, 'Failed to import JSON route.'));
      } finally {
        setImportLoading(false);
      }
    };
    const onImportJsonClick = () => {
      void onImportJson();
    };

    const fillFromExisting = async (routeId: number) => {
      try {
        const [route, media] = await Promise.all([getRoute(routeId), listAllRouteMedia(routeId)]);
        if (disposed) return;
        titleInput.value = route.title;
        slugInput.value = route.slug;
        locationNameInput.value = route.location_name || '';
        descriptionInput.value = route.description || '';
        distanceInput.value = route.distance_km || '';
        durationInput.value = String(route.duration_minutes || 0);
        gainInput.value = String(route.elevation_gain_m || 0);
        lossInput.value = String(route.elevation_loss_m || 0);
        difficultySelect.value = route.difficulty ? String(route.difficulty) : '';
        featuredInput.checked = Boolean(route.is_featured);
        statusSelect.value = route.status || 'draft';
        reviewCommentInput.value = route.review_comment || '';
        contentBlocksEditor.setBlocks(route.content_blocks || []);
        contentBlocksInput.value = stringifyContentBlocks(route.content_blocks || []);

        Array.from(categoriesSelect.options).forEach((option) => {
          option.selected = route.category_ids.includes(Number(option.value));
        });
        Array.from(tagsSelect.options).forEach((option) => {
          option.selected = route.tag_ids.includes(Number(option.value));
        });

        waypoints = (route.waypoints || []).map((item) => ({
          id: waypointCounter++,
          sort_order: item.sort_order,
          location_id: item.location ?? null,
          stop_type: item.stop_type || 'via',
          latitude: item.latitude || '',
          longitude: item.longitude || '',
          title: item.title || '',
          location_name: item.location_name || '',
          note: item.note || '',
        }));
        renderWaypoints();

        existingGallery = media.results
          .filter((item) => !isContentBlockMediaTitle(item.title))
          .map((item) => ({ id: item.id, url: item.file_large_url || item.file_url, title: item.title || '' }));
        renderGallery();
        selectedCover = null;
        selectedCoverMediaId = null;
        selectedCoverRemoteUrl = null;
        renderCoverPreview(route.cover_image ?? null);
      } catch (error) {
        showAlert('error', 'Failed to load route', getApiErrorMessage(error, 'Cannot load route for edit.'));
      }
    };

    const onTitleInput = () => {
      if (!slugTouched) {
        slugInput.value = slugify(titleInput.value);
      }
    };

    const onSlugInput = () => {
      slugTouched = slugInput.value.trim().length > 0;
    };

    void loadDictionaries().then(() => {
      if (isEdit) void fillFromExisting(editId);
    });

    if (!isEdit) {
      addWaypoint();
      addWaypoint();
    }

    titleInput.addEventListener('input', onTitleInput);
    slugInput.addEventListener('input', onSlugInput);
    addWaypointBtn.addEventListener('click', addWaypoint);
    waypointsBody.addEventListener('input', onWaypointsInput);
    waypointsBody.addEventListener('change', onWaypointsInput);
    waypointsBody.addEventListener('click', onWaypointsClick);
    coverFile.addEventListener('change', onCoverChange);
    galleryFiles.addEventListener('change', onGalleryChange);
    galleryPreview.addEventListener('click', onGalleryClick);
    galleryPreview.addEventListener('dragstart', onGalleryDragStart);
    coverDropZone.addEventListener('dragover', onCoverDragOver);
    coverDropZone.addEventListener('dragleave', onCoverDragLeave);
    coverDropZone.addEventListener('drop', onCoverDropEvent);
    imagePreviewModal.addEventListener('click', onPreviewModalClick);
    document.addEventListener('keydown', onPreviewKeydown);
    cancelBtn.addEventListener('click', () => navigate('/routes'));
    importBtn.addEventListener('click', onImportJsonClick);
    saveDraftBtn.addEventListener('click', () => void submit('draft'));
    publishBtn.addEventListener('click', () => void submit('publish'));
    form.addEventListener('submit', (event) => {
      event.preventDefault();
      void submit('draft');
    });

    return () => {
      disposed = true;
      titleInput.removeEventListener('input', onTitleInput);
      slugInput.removeEventListener('input', onSlugInput);
      addWaypointBtn.removeEventListener('click', addWaypoint);
      waypointsBody.removeEventListener('input', onWaypointsInput);
      waypointsBody.removeEventListener('change', onWaypointsInput);
      waypointsBody.removeEventListener('click', onWaypointsClick);
      coverFile.removeEventListener('change', onCoverChange);
      galleryFiles.removeEventListener('change', onGalleryChange);
      galleryPreview.removeEventListener('click', onGalleryClick);
      galleryPreview.removeEventListener('dragstart', onGalleryDragStart);
      coverDropZone.removeEventListener('dragover', onCoverDragOver);
      coverDropZone.removeEventListener('dragleave', onCoverDragLeave);
      coverDropZone.removeEventListener('drop', onCoverDropEvent);
      imagePreviewModal.removeEventListener('click', onPreviewModalClick);
      document.removeEventListener('keydown', onPreviewKeydown);
      importBtn.removeEventListener('click', onImportJsonClick);
      selectedGallery.forEach((item) => URL.revokeObjectURL(item.url));
      if (currentCoverObjectUrl) URL.revokeObjectURL(currentCoverObjectUrl);
      imagePreviewModal.remove();
      document.body.classList.remove('overflow-hidden');
    };
  }, [navigate, searchParams]);

  return (
    <>
      <Helmet>
        <title>Create Route | MotoJourney</title>
      </Helmet>
      <div ref={containerRef} dangerouslySetInnerHTML={{ __html: template }} />
    </>
  );
}
